import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Button,
  Tooltip,
  Divider,
  Slide,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import PrintIcon from '@mui/icons-material/Print';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

const FloatingStatusBar = ({
  selectedCount = 0,
  onClear,
  actions = [],
  itemLabel = 'item',
  show = false,
  position = 'bottom', // 'top' or 'bottom'
  sx = {},
  variant = 'light', // 'dark' or 'light'
}) => {
  // Draggable state
  const [isDragging, setIsDragging] = useState(false);
  const [dragPosition, setDragPosition] = useState(null);
  const dragRef = useRef({ startX: 0, startY: 0, initialX: 0, initialY: 0 });

  // Reset position when show changes
  useEffect(() => {
    if (!show) {
      setDragPosition(null);
    }
  }, [show]);
  // Theme configuration based on variant
  const themeConfig = {
    dark: {
      backgroundColor: '#2c2d30',
      textColor: '#ffffff',
      borderColor: '#3f4145',
      hoverBg: '#5f6064ff',
    },
    light: {
      backgroundColor: '#ffffff',
      textColor: '#2c2d30',
      borderColor: '#e0e0e0',
      hoverBg: '#f5f5f5',
    },
  };

  const theme = themeConfig[variant];

  // Predefined button configurations
  const buttonPresets = {
    delete: {
      icon: <DeleteIcon fontSize="small" />,
      color: 'error',
      tooltip: 'Delete selected items',
    },
    print: {
      icon: <PrintIcon fontSize="small" />,
      color: 'primary',
      tooltip: 'Print selected items',
    },
    approve: {
      icon: <CheckCircleIcon fontSize="small" />,
      color: 'success',
      tooltip: 'Approve selected items',
    },
    reject: {
      icon: <CancelIcon fontSize="small" />,
      color: 'error',
      tooltip: 'Reject selected items',
    },
  };

  // Process actions - merge presets with custom actions
  const processedActions = actions.map((action) => {
    const actionType = action.type?.toLowerCase();
    const preset = actionType ? buttonPresets[actionType] : null;

    if (preset) {
      // Use preset with overrides from action
      return {
        ...preset,
        ...action,
        label: action.label || actionType.charAt(0).toUpperCase() + actionType.slice(1),
        icon: action.icon || preset.icon,
        color: action.color || preset.color,
        tooltip: action.tooltip || preset.tooltip,
      };
    }

    // Return action as-is if no preset found
    return action;
  });

  // Position configuration
  const positionStyles = {
    bottom: {
      bottom: 30,
      top: 'auto',
    },
    top: {
      top: 24,
      bottom: 'auto',
    },
  };

  // Drag handlers
  const handleMouseDown = (e) => {
    e.stopPropagation();
    setIsDragging(true);
    const parentRect = e.currentTarget.closest('[data-floating-bar]').getBoundingClientRect();
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      initialX: dragPosition?.x ?? parentRect.left,
      initialY: dragPosition?.y ?? parentRect.top,
    };
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    
    const deltaX = e.clientX - dragRef.current.startX;
    const deltaY = e.clientY - dragRef.current.startY;
    
    const newX = dragRef.current.initialX + deltaX;
    const newY = dragRef.current.initialY + deltaY;
    
    // Constrain to window bounds
    const maxX = window.innerWidth - 400; // Approximate width
    const maxY = window.innerHeight - 100; // Approximate height
    
    setDragPosition({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY)),
    });
  }, [isDragging]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add global mouse event listeners for drag
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  if (!show) return null;

  return (
    <Slide direction="up" in={show} mountOnEnter unmountOnExit>
      <Box
        data-floating-bar
        sx={{
          position: 'fixed',
          ...(dragPosition
            ? {
                left: dragPosition.x,
                top: dragPosition.y,
                transform: 'none',
              }
            : {
                left: '35%',
                transform: 'translateX(-50%)',
                ...positionStyles[position],
              }),
          zIndex: 1300,
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.25)',
          borderRadius: 2,
          width: 'auto',
          userSelect: isDragging ? 'none' : 'auto',
          ...sx,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={0}
          sx={{
            backgroundColor: theme.backgroundColor,
            border: `1px solid ${theme.borderColor}`,
            borderRadius: 2,
            px: 1.5,
            py: 1,
            minWidth: { xs: '90vw', sm: 400 },
            maxWidth: '90vw',
          }}
        >
          {/* Selection Count */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            className="drag-handle"
            onMouseDown={handleMouseDown}
            sx={{
              px: 1.5,
              py: 0.5,
              minWidth: 120,
              '&:hover': {
                backgroundColor: variant === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
              },
              userSelect: 'none',
            }}
          >
            <DragIndicatorIcon 
              sx={{ 
                fontSize: 18,
                color: theme.textColor,
                opacity: 0.5,
              }} 
            />
            <Typography
              variant="body2"
              sx={{
                color: theme.textColor,
                fontWeight: 600,
                fontSize: '0.875rem',
              }}
            >
              {selectedCount} {itemLabel}
              {selectedCount !== 1 ? 's' : ''} selected
            </Typography>
            
            <Tooltip title="Clear selection" fontSize="small">
              <IconButton
                size="small"
                onClick={onClear}
                sx={{
                  color: theme.textColor,
                  '&:hover': {
                    backgroundColor: theme.hoverBg,
                  },
                  ml: 0.5,
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>

          {/* Divider */}
          {actions.length > 0 && (
            <Divider
              orientation="vertical"
              flexItem
              sx={{
                borderColor: theme.borderColor,
                mx: 1,
              }}
            />
          )}

          {/* Action Buttons */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{
              flex: 1,
              overflowX: 'auto',
              '&::-webkit-scrollbar': {
                height: 4,
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: theme.borderColor,
                borderRadius: 2,
              },
            }}
          >
            {processedActions.map((action, index) => (
              <React.Fragment key={index}>
                <Tooltip title={action.tooltip || action.label}  >
                  <span>
                    <Button
                      size="small"
                      variant={action.variant || 'text'}
                      color={action.color || 'inherit'}
                      hidden={action.disabled}
                      disabled={action.disabled || false}
                      onClick={action.onClick}
                      startIcon={action.icon}
                      sx={{
                        color: theme.textColor,
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '1rem',
                        px: 1.5,
                        py: 0.5,
                        minWidth: 'auto',
                        whiteSpace: 'nowrap',
                        '&:hover': {
                          backgroundColor: theme.hoverBg,
                        },
                        '&.Mui-disabled': {
                          color: variant === 'dark' ? '#727272ff' : '#ccc',
                        },
                        ...(action.color === 'error' && {
                          color: '#f44336',
                          '&:hover': {
                            backgroundColor: 'rgba(177, 99, 93, 0.08)',
                          },
                        }),
                        ...(action.color === 'primary' && {
                          color: '#2196f3',
                          '&:hover': {
                            backgroundColor: 'rgba(104, 178, 238, 0.08)',
                          },
                        }),
                        ...(action.color === 'success' && {
                          color: '#4caf50',
                          '&:hover': {
                            backgroundColor: 'rgba(136, 173, 138, 0.08)',
                          },
                        }),
                      }}
                    >
                      {action.label}
                    </Button>
                  </span>
                </Tooltip>
                
                {/* Optional divider after action */}
                {action.showDivider && index < actions.length - 1 && (
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{
                      borderColor: theme.borderColor,
                      mx: 0.5,
                      height: 24,
                      alignSelf: 'center',
                    }}
                  />
                )}
              </React.Fragment>
            ))}
          </Stack>
        </Stack>
      </Box>
    </Slide>
  );
};

export default FloatingStatusBar;